// Ported from packages/desk/src/mobile/gestures.ts (docs/PLAN-2026-09-21-mobile-os-shell.md, step 4). Same file name and props
// as the desk copy, the dk- prefix renamed rdr-, so a fix in one is a copy in the other. Radar never imports @gamo/desk.
/**
 * The phone's gestures: a long press on an icon (quick actions), a swipe up on the home screen (the App Library) and a
 * swipe down on a sheet (close it). The timing and the thresholds are plain functions and a small controller, so they
 * are tested with fake timers; the hooks only wire them to pointer events.
 */
import { useEffect, useRef, type MouseEvent, type PointerEvent, type TouchEvent } from "react";

/** How long a press must hold to become a long press, the platform's own feel. */
export const LONG_PRESS_MS = 500;
/** A finger that moves farther than this is scrolling or swiping, not pressing. */
export const MOVE_TOLERANCE_PX = 10;
/** How far a swipe must travel to count. */
export const SWIPE_MIN_PX = 60;

export interface LongPressController {
  down(x: number, y: number): void;
  move(x: number, y: number): void;
  /** Ends the press: "long" when the long press already fired, "tap" when it was short and still, "cancelled" otherwise. */
  up(): "tap" | "long" | "cancelled";
  cancel(): void;
  /** Whether the long press fired during the current or last press (a click that follows it is swallowed). */
  fired(): boolean;
}

export function longPress(onLongPress: () => void, ms = LONG_PRESS_MS, tolerance = MOVE_TOLERANCE_PX): LongPressController {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let start: { x: number; y: number } | null = null;
  let didFire = false;
  let moved = false;
  const clear = () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };
  return {
    down(x, y) {
      clear();
      start = { x, y };
      didFire = false;
      moved = false;
      timer = setTimeout(() => {
        timer = null;
        didFire = true;
        onLongPress();
      }, ms);
    },
    move(x, y) {
      if (!start || moved) return;
      if (Math.hypot(x - start.x, y - start.y) > tolerance) {
        moved = true;
        clear();
      }
    },
    up() {
      const was = didFire ? "long" : moved || !start ? "cancelled" : "tap";
      clear();
      start = null;
      // The click that follows a long press comes right away or not at all (a touch browser may skip it); a keyboard
      // press a moment later is a real press and must not be swallowed.
      if (didFire) setTimeout(() => (didFire = false), 400);
      return was;
    },
    cancel() {
      clear();
      start = null;
      moved = true;
    },
    fired: () => didFire,
  };
}

/** Which way a finished drag went, if it counts as a swipe: mostly vertical, far enough. */
export function swipeOf(dx: number, dy: number, min = SWIPE_MIN_PX): "up" | "down" | null {
  if (Math.abs(dy) < min || Math.abs(dy) < Math.abs(dx) * 1.5) return null;
  return dy < 0 ? "up" : "down";
}

/**
 * Pointer handlers for an icon: a tap runs `onTap` (through the button's own click, so the keyboard works the same),
 * a long press runs `onLongPress` and swallows the click that follows it, and the context-menu key or a right click
 * runs `onLongPress` as well.
 */
export function useLongPress(onLongPress: () => void, disabled = false) {
  const cb = useRef(onLongPress);
  useEffect(() => {
    cb.current = onLongPress;
  });
  const ctl = useRef<LongPressController | null>(null);
  ctl.current ??= longPress(() => cb.current());
  useEffect(() => () => ctl.current?.cancel(), []);
  if (disabled) return {};
  return {
    onPointerDown: (e: PointerEvent) => {
      if (e.button !== 0) return;
      ctl.current!.down(e.clientX, e.clientY);
    },
    onPointerMove: (e: PointerEvent) => ctl.current!.move(e.clientX, e.clientY),
    onPointerUp: () => void ctl.current!.up(),
    onPointerCancel: () => ctl.current!.cancel(),
    onPointerLeave: () => ctl.current!.cancel(),
    onClickCapture: (e: MouseEvent) => {
      // The click that ends a long press is not a tap.
      if (ctl.current!.fired()) {
        e.preventDefault();
        e.stopPropagation();
        ctl.current!.cancel();
      }
    },
    onContextMenu: (e: MouseEvent) => {
      e.preventDefault();
      // A touch browser fires this for the same long press the timer already caught: once is enough.
      if (ctl.current!.fired()) return;
      ctl.current!.cancel();
      cb.current();
    },
  };
}

/**
 * Calls `onSwipe` when a finger's drag on the element ends as a swipe. Touch events, not pointer events: a phone hands
 * a vertical drag over a scrolling area to its own scrolling and cancels the pointer, while the touch still ends with
 * its position. Mouse drags are left to text selection.
 */
export function useSwipe(onSwipe: (dir: "up" | "down") => void) {
  const start = useRef<{ x: number; y: number; id: number } | null>(null);
  return {
    onTouchStart: (e: TouchEvent) => {
      const t = e.changedTouches[0];
      start.current = e.touches.length === 1 && t ? { x: t.clientX, y: t.clientY, id: t.identifier } : null;
    },
    onTouchEnd: (e: TouchEvent) => {
      const s = start.current;
      start.current = null;
      const t = s ? Array.from(e.changedTouches).find((x) => x.identifier === s.id) : undefined;
      if (!s || !t) return;
      const dir = swipeOf(t.clientX - s.x, t.clientY - s.y);
      if (dir) onSwipe(dir);
    },
    onTouchCancel: () => {
      start.current = null;
    },
  };
}
